<template>
  <div class="group-month-container">
    <div class="content-header">
      <div class="header-icon">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
          <circle cx="9" cy="7" r="4"/>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
          <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
        </svg>
      </div>
      <h2>Havi csoportonkénti fogyás</h2>
      <p>Válasszon egy csoportot a részletes adatok megtekintéséhez</p>
    </div>

    <!-- Show loading message or spinner while waiting for the data -->
    <div v-if="loading" class="loading-container">
      <div class="loading-spinner"></div>
      <p>Adatok betöltése...</p>
    </div>

    <!-- Display list of groups -->
    <div v-if="!loading && groups.length > 0" class="groups-section">
      <div class="section-title">
        <h3>Válasszon egy csoportot:</h3>
      </div>
      <div class="groups-grid">
        <div 
          v-for="group in groups" 
          :key="group.id" 
          class="group-card"
          @click="selectGroup(group)"
        >
          <div class="group-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
          </div>
          <div class="group-content">
            <h4>{{ group.name }}</h4>
            <p>Kattintson a részletek megtekintéséhez</p>
          </div>
          <div class="group-arrow">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </div>
        </div>
      </div>
    </div>

    <!-- Show selected group data -->
    <div v-if="selectedGroup && attendanceData.length > 0" class="data-section">
      <div class="data-header">
        <div class="data-title">
          <h3>Adatok a(z) {{ selectedGroup.name }} csoporthoz</h3>
          <p>{{ attendanceData.length }} hónap adata • {{ teachersData.length }} tanár • {{ childrenData.length }} gyermek</p>
        </div>
        <button @click="closeData" class="close-data-btn">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>
      
      <div class="data-table-container">
        <table class="data-table">
          <thead>
            <tr>
              <th>Hónap</th>
              <th>Összesítve</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="entry in attendanceData" :key="entry.count_id">
              <td>{{ getMonthName(entry.count_id) }}</td>
              <td class="count-cell">{{ entry.total_attended_count }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Teachers Section -->
      <div v-if="teachersData.length > 0" class="teachers-section">
        <h4>Tanárok részvételi statisztikái</h4>
        <div class="teachers-table-container">
          <table class="data-table">
            <thead>
              <tr>
                <th>Tanár neve</th>
                <th>Összes részvétel</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="teacher in teachersData" :key="teacher.id">
                <td>{{ teacher.name }}</td>
                <td class="count-cell">{{ teacher.totalAttendance }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Children Section -->
      <div v-if="childrenData.length > 0" class="children-section">
        <h4>Gyermekek részvételi statisztikái</h4>
        <div class="children-table-container">
          <table class="data-table">
            <thead>
              <tr>
                <th>Gyermek neve</th>
                <th>Összes részvétel</th>
                <th v-for="month in 12" :key="month">{{ getMonthName(month).substring(0, 3) }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="child in childrenData" :key="child.id">
                <td>{{ child.name }}</td>
                <td class="count-cell">{{ child.totalAttendance }}</td>
                <td v-for="monthData in child.attendanceMonths" :key="monthData.month" :class="monthData.attended ? 'attended-cell' : 'not-attended-cell'">
                  {{ monthData.attended ? '✓' : '✗' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// Import axios
import axios from 'axios';

export default {
  name: 'AttendanceTable',
  data() {
    return {
      attendanceData: [], // Monthly attendance data
      teachersData: [], // Teachers attendance data
      childrenData: [], // Children attendance data
      loading: true, // Loading state to control when to show the table or the loading message
      currentYear: new Date().getFullYear(), // Dynamically get the current year
      groups: [], // Group list to be populated from the backend
      selectedGroup: null, // Store the selected group
    };
  },
  methods: {
    // Method to fetch group data
    async fetchGroupData() {
      try {
        const response = await fetch(this.$BACKEND_HOST + '/api/settings/groups/info');
        if (!response.ok) {
          throw new Error('Failed to fetch groups');
        }
        const data = await response.json();
        this.groups = data.groups;
        this.loading = false;
      } catch (err) {
        this.error = err.message;
        this.loading = false;
      }
    },

    // Method to select a group and fetch its attendance data (monthly)
    selectGroup(group) {
      this.selectedGroup = group;
      this.fetchAttendanceData(group.id);
    },

    // Method to fetch attendance data for monthly view
    fetchAttendanceData(groupId) {
      this.loading = true;
      axios.post(this.$BACKEND_HOST + '/api/getstatistics/groupmonth', { year: this.currentYear, grp_id: groupId })
        .then(response => {
          this.attendanceData = response.data.monthly || [];
          this.teachersData = response.data.teachers || [];
          this.childrenData = response.data.children || [];
        })
        .catch(error => {
          console.error('There was an error fetching the attendance data:', error);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    // Method to get month name by count_id (1-12) in Hungarian
    getMonthName(monthNumber) {
      const months = [
        "Január", "Február", "Március", "Április", "Május", "Június",
        "Július", "Augusztus", "Szeptember", "Október", "November", "December"
      ];
      return months[monthNumber - 1];
    },

    // Method to close the group data view
    closeData() {
      this.attendanceData = [];
      this.teachersData = [];
      this.childrenData = [];
      this.selectedGroup = null;
    }
  },
  mounted() {
    this.fetchGroupData(); // Fetch the groups when the component is mounted
  }
};
</script>

<style scoped>
.group-month-container {
  padding: 0;
  max-width: 100%;
}

.content-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid var(--color-border);
}

.header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  border-radius: 12px;
  color: white;
  flex-shrink: 0;
  box-shadow: var(--shadow-sm);
}

.content-header h2 {
  color: var(--color-heading);
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
}

.content-header p {
  color: var(--color-text-muted);
  font-size: 0.95rem;
  margin: 0;
  margin-left: auto;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  gap: 1rem;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid var(--color-border);
  border-top: 4px solid var(--primary-blue);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-container p {
  color: var(--color-text-muted);
  font-size: 1rem;
  margin: 0;
}

.groups-section {
  margin-bottom: 2rem;
}

.section-title {
  margin-bottom: 1.5rem;
}

.section-title h3 {
  color: var(--color-heading);
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
}

.groups-grid {
  display: grid;
  gap: 1rem;
}

.group-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem;
  background: var(--card-bg);
  border-radius: 12px;
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--color-border);
  cursor: pointer;
  transition: all 0.3s ease;
}

.group-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
  border-color: var(--primary-blue);
}

.group-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 45px;
  height: 45px;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  border-radius: 10px;
  color: white;
  flex-shrink: 0;
  box-shadow: var(--shadow-sm);
}

.group-content {
  flex: 1;
}

.group-content h4 {
  color: var(--color-heading);
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0 0 0.25rem 0;
}

.group-content p {
  color: var(--color-text-muted);
  font-size: 0.875rem;
  margin: 0;
  line-height: 1.4;
}

.group-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  background: var(--color-background-mute);
  border-radius: 50%;
  color: var(--primary-blue);
  flex-shrink: 0;
  transition: all 0.3s ease;
}

.group-card:hover .group-arrow {
  background: var(--primary-blue);
  color: white;
  transform: translateX(4px);
}

.data-section {
  background: var(--card-bg);
  border-radius: 16px;
  box-shadow: var(--shadow);
  border: 1px solid var(--color-border);
  overflow: hidden;
}

.data-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  background: var(--color-background-mute);
  border-bottom: 1px solid var(--color-border);
}

.data-title h3 {
  color: var(--color-heading);
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 0.25rem 0;
}

.data-title p {
  color: var(--color-text-muted);
  font-size: 0.875rem;
  margin: 0;
}

.close-data-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 35px;
  height: 35px;
  background: var(--color-background-mute);
  border: 1px solid var(--color-border);
  border-radius: 50%;
  color: var(--color-text-muted);
  cursor: pointer;
  transition: all 0.3s ease;
}

.close-data-btn:hover {
  background: var(--error);
  color: white;
  border-color: var(--error);
  transform: scale(1.1);
}

.data-table-container {
  padding: 1.5rem 2rem;
  overflow-x: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  background: var(--card-bg);
}

.data-table thead {
  background: var(--color-background-mute);
}

.data-table th {
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: var(--color-heading);
  border-bottom: 2px solid var(--color-border);
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.data-table td {
  padding: 1rem;
  border-bottom: 1px solid var(--color-border);
  color: var(--color-text);
  font-size: 0.95rem;
}

.data-table tbody tr:hover {
  background: var(--color-background-mute);
}

.count-cell {
  font-weight: 600;
  color: var(--primary-blue);
  text-align: center;
}

.teachers-section,
.children-section {
  margin-top: 2rem;
}

.teachers-section h4,
.children-section h4 {
  color: var(--color-heading);
  font-size: 1.125rem;
  font-weight: 600;
  margin-bottom: 1rem;
  margin-left: 2rem;
}

.teachers-table-container,
.children-table-container {
  padding: 0 2rem;
  overflow-x: auto;
}

.attended-cell {
  text-align: center;
  color: var(--success);
  font-weight: bold;
}

.not-attended-cell {
  text-align: center;
  color: var(--error);
  font-weight: bold;
}

/* Responsive design */
@media (max-width: 768px) {
  .content-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
  
  .content-header p {
    margin-left: 0;
  }
  
  .group-card {
    padding: 1rem;
    gap: 0.75rem;
  }
  
  .group-icon {
    width: 40px;
    height: 40px;
  }
  
  .group-content h4 {
    font-size: 1rem;
  }
  
  .group-content p {
    font-size: 0.8rem;
  }
  
  .data-header {
    padding: 1rem 1.5rem;
  }
  
  .data-title h3 {
    font-size: 1.125rem;
  }
  
  .data-table-container {
    padding: 1rem 1.5rem;
  }
  
  .data-table th,
  .data-table td {
    padding: 0.75rem;
    font-size: 0.875rem;
  }
}

@media (max-width: 480px) {
  .group-card {
    padding: 0.75rem;
    gap: 0.5rem;
  }
  
  .group-icon {
    width: 35px;
    height: 35px;
  }
  
  .group-content h4 {
    font-size: 0.9rem;
  }
  
  .group-content p {
    font-size: 0.75rem;
  }
  
  .group-arrow {
    width: 25px;
    height: 25px;
  }
  
  .data-header {
    padding: 0.75rem 1rem;
  }
  
  .data-title h3 {
    font-size: 1rem;
  }
  
  .data-table-container {
    padding: 0.75rem 1rem;
  }
  
  .data-table th,
  .data-table td {
    padding: 0.5rem;
    font-size: 0.8rem;
  }
}
</style>
